// Loja dos NPCs (o botão "Mercado"): comprar suprimentos, vender loot e comprar bênçãos, com visual de balcão de comerciante.
// Poções e runas não são vendidas pelo jogador (são cobradas por uso). O botão "Mercado de jogadores" abre o Market.tsx.
// CSS em client/css-parts/market.css (prefixo .sp-).
import { useEffect, useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { RARITY_NAMES, npcSellUnit } from '../../../../shared/src/game/items';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';
import { BLESSINGS, blessingPrice, countBits, potionVocOk } from '../../../../shared/src/game/combat';
import { FOOD_SECONDS } from '../../../../shared/src/game/helper';
const FOOD_NAMES = new Set(Object.keys(FOOD_SECONDS).map((k) => k.toLowerCase()));

type Cat = 'potions' | 'runes' | 'ammo' | 'equipment' | 'containers' | 'decoration';
interface Offer { typeId: number; name: string; price: number; level?: number; cat: Cat }
const CATS: Array<[Cat, string, string]> = [['equipment', 'Equipamentos', '🛡️'], ['containers', 'Mochilas', '🎒'], ['decoration', 'Decoração', '🪴']];
const COINS: Record<string, number> = { 'gold coin': 1, 'platinum coin': 100, 'crystal coin': 10000 };
const RAR: Array<[number, string]> = [[0, 'Comum'], [1, 'Raro'], [2, 'Épico'], [3, 'Lendário']];
const n0 = (v: number) => v.toLocaleString('pt-BR');

export function Shop(props: {
  items: ItemInstance[]; keep?: number[]; level: number; vocation?: string; bank: number; blessings: number; vip: boolean; inCity: boolean; huntSellAt: number;
  onBuy: (typeId: number, count: number) => void; onSell: (ids: number[], maxRarity?: number) => void; onDeposit: () => void; onBless: (enhanced: boolean) => void; onPlayers?: () => void;
  tab?: 'buy' | 'sell' | 'bless';
}) {
  const [tab, setTab] = useState<'buy' | 'sell' | 'bless'>(props.tab ?? (props.inCity ? 'buy' : 'sell'));
  const [offers, setOffers] = useState<Offer[]>([]);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [cat2, setCat2] = useState<Cat>('equipment');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [maxR, setMaxR] = useState(0);
  const [help, setHelp] = useState(false);
  // poções que a vocation não usa nem aparecem na loja
  useEffect(() => { fetch('/api/shop').then((r) => r.json()).then((l: Offer[]) => setOffers(l.filter((o) => !props.vocation || potionVocOk(o.name, props.vocation)))).catch(() => {}); }, [props.vocation]);
  const cat = catalogNow();
  const byId = new Map(props.items.map((i) => [i.id, i]));
  const inDepot = (i: ItemInstance) => { let c: ItemInstance | undefined = i; while (c) { if (c.place === 'depot' || c.place === 'inbox') return true; if (c.place === 'equip') return false; c = c.parentId ? byId.get(c.parentId) : undefined; } return false; };
  const carried = props.items.filter((i) => !inDepot(i));
  const coins = carried.reduce((s, i) => s + (COINS[cat?.get(i.typeId)?.name.toLowerCase() ?? ''] ?? 0) * i.count, 0);
  const money = coins + props.bank;
  const sellWait = props.inCity ? 0 : Math.max(0, props.huntSellAt - Date.now());
  const price = (i: ItemInstance) => { const t = cat?.get(i.typeId); if (i.bound) return 0; if (/ potion$/i.test(t?.name ?? '')) return i.count; if (i.place === 'pouch') return 0; const v = Math.max(t?.npcPrice ?? 0, t?.value ?? 0); return (v || t?.minSell) && !i.bound && !COINS[t!.name.toLowerCase()] ? npcSellUnit(v, i.rarity, offers.some((o) => o.typeId === i.typeId), t?.minSell) * i.count : 0; };
  const sellable = carried.filter((i) => i.place !== 'equip' && price(i) > 0 && !props.items.some((c) => c.parentId === i.id));
  const pickedTotal = sellable.filter((i) => picked.has(i.id)).reduce((s, i) => s + price(i), 0);
  // o que o "Vender loot" leva (as mesmas regras do servidor): até a raridade escolhida, sem poções, runas, comida, mochilas e os marcados para guardar
  const bulk = sellable.filter((i) => { const n = cat?.get(i.typeId)?.name ?? ''; return i.rarity <= maxR && i.place !== 'pouch' && !/potion|rune/i.test(n) && !cat?.get(i.typeId)?.container && !props.keep?.includes(i.typeId) && !FOOD_NAMES.has(n.toLowerCase()); });
  const bulkTotal = bulk.reduce((s, i) => s + price(i), 0);
  const bulkIds = new Set(bulk.map((i) => i.id));
  const bulkCount = bulk.reduce((s, i) => s + i.count, 0);
  const commonLeft = 5 - countBits(props.blessings & 0b11111), enhLeft = 2 - countBits(props.blessings & 0b1100000);
  const list = offers.filter((o) => (q ? o.name.toLowerCase().includes(q.toLowerCase()) : o.cat === cat2));

  return (
    <div class="sp">
      {/* cabeçalho: toldo listrado + balcão de madeira, com os saldos */}
      <div class="sp-head">
        <div class="sp-awning" aria-hidden="true" />
        <div class="sp-head-in">
          <span class="sp-sign" aria-hidden="true">🏪</span>
          <div class="sp-head-txt">
            <div class="sp-title">Balcão do Mercador <button class="sp-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
            <small>Suprimentos, compra do seu loot e bênçãos.</small>
          </div>
          <div class="sp-wallet" title="Banco + moedas na mochila">
            <span class="sp-purse"><i class="sp-coin" /><span><small>Banco</small><b>{n0(props.bank)} gp</b></span></span>
            {coins > 0 && <span class="sp-purse bag"><i class="sp-coin" /><span><small>Na mochila</small><b>{n0(coins)} gp</b></span></span>}
          </div>
        </div>
      </div>
      {help && (
        <div class="sp-steps">
          <div><i>🛒</i><b>Comprar</b><small>Escolha a categoria, ajuste a quantidade e clique no preço. Compras só na cidade.</small></div>
          <div><i>💰</i><b>Vender loot</b><small>Escolha até qual raridade vender e venda tudo de uma vez, ou toque nos itens para vender só alguns. Na hunt: {props.vip ? 'a cada 10 min (VIP)' : 'a cada 20 min (VIP: 10 min)'}.</small></div>
          <div><i>🕊️</i><b>Bênçãos</b><small>Reduzem a perda de experiência e, com as 5 comuns, você não perde itens ao morrer.</small></div>
        </div>
      )}

      <div class="sp-nav">
        <button class={'tb-button' + (tab === 'buy' ? ' green' : '')} onClick={() => setTab('buy')}>Comprar</button>
        <button class={'tb-button' + (tab === 'sell' ? ' green' : '')} onClick={() => setTab('sell')}>Vender</button>
        <button class={'tb-button' + (tab === 'bless' ? ' green' : '')} onClick={() => setTab('bless')}>Bênçãos</button>
        <button class="tb-button sp-players" title="Mercado entre jogadores: ofertas de compra e venda, como o Market do Tibia" onClick={() => props.onPlayers?.()}>⚖️ Mercado de jogadores</button>
      </div>
      {coins > 0 && (
        <div class="sp-note gold"><span>Você tem <b>{n0(coins)} gp</b> em moedas na mochila.</span><button class="tb-button" onClick={props.onDeposit}>Depositar as moedas da mochila no banco</button></div>
      )}
      {!props.inCity && tab !== 'sell' && <div class="sp-note warn">Você está numa hunt: compras e bênçãos só na cidade. Aqui dá para vender o loot ({props.vip ? 'VIP: a cada 10 min' : 'a cada 20 min; VIP a cada 10 min'}).</div>}

      {tab === 'buy' && (
        <>
          <div class="sp-cats">
            {CATS.map(([id, label, icon]) => (
              <button key={id} class={'sp-cat' + (cat2 === id && !q ? ' on' : '')} onClick={() => { setCat2(id); setQ(''); }}><i>{icon}</i>{label}<span>{offers.filter((o) => o.cat === id).length}</span></button>
            ))}
            <input class="sp-input" placeholder="🔎 Buscar item" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          </div>
          <div class="sp-grid">
            {list.map((o) => {
              const n = counts[o.typeId] ?? (o.cat === 'potions' ? 50 : o.cat === 'ammo' ? 100 : o.cat === 'runes' ? 50 : 1);
              const locked = !!o.level && props.level < o.level;
              return (
                <div key={o.typeId} class={'sp-card' + (locked ? ' locked' : '')}>
                  <span class="sp-slot"><ItemImg typeId={o.typeId} count={1} /></span>
                  <b class="sp-name" title={o.name}>{o.name}</b>
                  <span class="sp-each"><i class="sp-coin" />{n0(o.price)} <small>gp cada</small></span>
                  {o.level ? <small class={'sp-lvl' + (locked ? ' bad' : '')}>{locked ? `Requer level ${o.level}` : `Level ${o.level}+`}</small> : <small class="sp-lvl">&nbsp;</small>}
                  <div class="sp-buy">
                    <input class="sp-input num" type="number" min={1} max={1000} value={n} onInput={(e) => setCounts({ ...counts, [o.typeId]: Math.max(1, Math.min(1000, Number((e.target as HTMLInputElement).value) || 1)) })} />
                    <button class="tb-button green" disabled={!props.inCity || locked || money < o.price * n} onClick={() => props.onBuy(o.typeId, n)}>{n0(o.price * n)} gp</button>
                  </div>
                </div>
              );
            })}
            {!list.length && <div class="sp-empty">{offers.length ? 'Nenhum item encontrado.' : 'Carregando a loja...'}</div>}
          </div>
        </>
      )}

      {tab === 'sell' && (
        <>
          {/* resumo da venda de loot, bem visível */}
          <div class="sp-loot">
            <div class="sp-loot-l">
              <span class="sp-loot-cap">💰 Vender loot</span>
              <div class="sp-rar">
                <small>até</small>
                {RAR.map(([r, l]) => <button key={r} class={`sp-r${r}` + (maxR === r ? ' on' : '')} onClick={() => setMaxR(r)} title={`Vende o loot até a raridade ${l}`}>{l}</button>)}
              </div>
              <small class="sp-loot-note">Não vende poções, runas, comida, mochilas nem os itens marcados como Não vender. Os itens que entram têm o selo 💰 abaixo.</small>
            </div>
            <div class="sp-loot-r">
              <small>{n0(bulk.length)} {bulk.length === 1 ? 'pilha' : 'pilhas'} · {n0(bulkCount)} itens</small>
              <b class="sp-loot-total"><i class="sp-coin big" />{n0(bulkTotal)} <small>gp</small></b>
              <button class="tb-button green" disabled={!bulk.length || sellWait > 0} onClick={() => props.onSell([], maxR)} title="Vende o loot da mochila até a raridade escolhida (não vende poções, runas, comida, mochilas nem os itens marcados como Não vender)">Vender loot ({n0(bulkTotal)} gp)</button>
            </div>
          </div>
          {sellWait > 0 && <div class="sp-note warn">⏳ Na hunt você poderá vender de novo em {Math.ceil(sellWait / 60000)} min. Na cidade a venda é livre.</div>}

          <div class="sp-sec">Sua mochila <small>· toque para escolher itens avulsos</small></div>
          <div class="sp-grid sell">
            {sellable.map((i) => (
              <div key={i.id} class={'sp-card sell' + (picked.has(i.id) ? ' picked' : '') + (i.rarity ? ` sp-rc${i.rarity}` : '')}
                onClick={() => setPicked((p) => { const n = new Set(p); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; })}>
                <span class={'sp-slot' + (i.rarity ? ` sp-rs${i.rarity}` : '')}><ItemImg typeId={i.typeId} count={i.count} /></span>
                <span class="sp-name" title={cat?.get(i.typeId)?.name}>{i.count > 1 ? `${i.count}x ` : ''}{cat?.get(i.typeId)?.name}</span>
                {i.rarity ? <small class={`sp-rt${i.rarity}`}>{RARITY_NAMES[i.rarity]}</small> : null}
                <b class="sp-val">{n0(price(i))} gp</b>
                {bulkIds.has(i.id) && <em class="sp-inbulk" title="Entra no Vender loot">💰</em>}
                {picked.has(i.id) && <em class="sp-check">✓</em>}
              </div>
            ))}
            {!sellable.length && <div class="sp-empty">Nada vendável na mochila. Itens equipados, no depot e na Store Box não aparecem aqui.</div>}
          </div>
          <div class="sp-foot">
            <span>Selecionados: <b>{picked.size}</b> · <b class="sp-goldtxt">{n0(pickedTotal)} gp</b></span>
            <button class="tb-button green" disabled={!picked.size || sellWait > 0} onClick={() => { props.onSell([...picked]); setPicked(new Set()); }}>Vender selecionados ({n0(pickedTotal)} gp)</button>
          </div>
        </>
      )}

      {tab === 'bless' && (
        <div class="sp-bless">
          <p class="sp-bless-txt">Ao morrer, cada bênção reduz em 8% a perda de experiência. Com as 5 bênçãos comuns você não perde itens. As bênçãos são gastas na morte. O Amulet of Loss também protege os itens (e some).</p>
          <div class="sp-bgrid">
            {BLESSINGS.map((b, i) => {
              const has = !!(props.blessings & (1 << i));
              return (
                <div key={b} class={'sp-blessing' + (has ? ' has' : '') + (i >= 5 ? ' enh' : '')}>
                  <i>{has ? '🕊️' : '◌'}</i>
                  <b>{b}</b>
                  <small>{i < 5 ? 'comum' : 'aprimorada'}</small>
                  <span class={has ? 'ok' : 'muted'}>{has ? 'Você tem' : 'Não tem'}</span>
                </div>
              );
            })}
          </div>
          <div class="sp-foot">
            <button class="tb-button green" disabled={!props.inCity || commonLeft === 0} onClick={() => props.onBless(false)}>
              Comprar as comuns que faltam ({n0(blessingPrice(props.level) * commonLeft)} gp)</button>
            <button class="tb-button" disabled={!props.inCity || enhLeft === 0} onClick={() => props.onBless(true)}>
              Aprimoradas ({n0(blessingPrice(props.level, true) * enhLeft)} gp)</button>
          </div>
        </div>
      )}
    </div>
  );
}
